/**
 * API helpers for the per-user egress credential vault (third-party OBO).
 *
 * Mirrors the CSRF pattern used elsewhere (ServerConfigModal): mutating calls
 * fetch /api/auth/csrf-token and send it as X-CSRF-Token. All calls rely on the
 * session cookie for auth (withCredentials is the axios default here).
 */
import axios from 'axios';

export interface EgressConnection {
  provider: string;
  server_path: string;
  scopes: string[];
  expires_at: string | null;
  status: string;
  last_refreshed_at: string | null;
  /** Slugged account id, or null for the legacy account-less connection
   * (every connection that existed before multi-account support). */
  account_id: string | null;
  /** User-typed display label account_id was slugged from. */
  account_label: string | null;
  /** Whether this is the account an agent's tool call auto-uses. A legacy
   * (account_id === null) entry is always implicitly the default. */
  is_default: boolean;
  /** Whether this connection's owner has opted in to let the background
   * tool-discovery job use it for a read-only tools/list catalog refresh.
   * Never used for real per-user access. */
  consents_to_tool_discovery: boolean;
  /** AGENTGOV-164: read/write classification derived from the scopes the
   * provider itself granted (not self-declared). 'unknown' (or missing, for
   * responses predating this field) means we can't classify it yet -- never
   * assume read-only for an unclassified provider or an empty scope list. */
  credential_scope?: 'unknown' | 'verified_read_only' | 'verified_write_capable';
}

export interface AvailableEgressServer {
  server_path: string;
  server_name: string;
  provider: string;
}

/** Result of probing a server's own OAuth discovery metadata (RFC 9728/8414/7591). */
export interface DiscoveredOAuthConfig {
  authorize_url: string;
  token_url: string;
  scopes_supported: string[];
  client_id: string | null;
  client_secret: string | null;
  auto_registered: boolean;
}

export interface DiscoverEgressAuthResult {
  /** True when discovery found a client_id via Dynamic Client Registration and
   * the config was already saved -- nothing left for the owner to fill in. */
  autoConfigured: boolean;
  discovered: DiscoveredOAuthConfig;
}

/** Thrown by discoverEgressAuth when the server doesn't support auto-discovery (HTTP 422). */
export class OAuthDiscoveryUnsupportedError extends Error {}

/**
 * Probe a server's own `.well-known` OAuth metadata instead of hand-typing
 * Custom OIDC fields. Admin only; the server must already be registered.
 *
 * When `autoConfigured` is true, the backend already persisted a working
 * `custom` provider config (including an auto-registered client_id/secret) --
 * the caller should just refresh its view of the server's egress config.
 * When false, `discovered` still carries authorize_url/token_url to pre-fill
 * the manual Custom OIDC form; the owner must still supply a client_id
 * (from an OAuth app they create by hand) before saving.
 */
export async function discoverEgressAuth(
  serverPath: string,
  scopes: string[]
): Promise<DiscoverEgressAuthResult> {
  const headers = await csrfHeaders();
  try {
    const resp = await axios.post(
      `/api/servers${serverPath}/egress-auth/discover`,
      { scopes },
      { headers }
    );
    return {
      autoConfigured: Boolean(resp.data.auto_configured),
      discovered: resp.data.discovered as DiscoveredOAuthConfig,
    };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 422) {
      throw new OAuthDiscoveryUnsupportedError(
        (err.response.data?.detail as string) ||
          'This server does not support automatic OAuth discovery.'
      );
    }
    throw err;
  }
}

/**
 * Same discovery chain as discoverEgressAuth, but for the Register form,
 * before the server exists as a registry entry -- probes the given URL
 * directly instead of reading proxy_pass_url off an already-saved server.
 * Never persists anything; the caller fills the result into its own
 * in-progress registration form state.
 */
export async function discoverEgressAuthForUrl(
  baseUrl: string,
  scopes: string[]
): Promise<DiscoverEgressAuthResult> {
  const headers = await csrfHeaders();
  try {
    const resp = await axios.post(
      '/api/egress-auth/discover',
      { base_url: baseUrl, scopes },
      { headers }
    );
    return {
      autoConfigured: Boolean(resp.data.auto_configured),
      discovered: resp.data.discovered as DiscoveredOAuthConfig,
    };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 422) {
      throw new OAuthDiscoveryUnsupportedError(
        (err.response.data?.detail as string) ||
          'This server does not support automatic OAuth discovery.'
      );
    }
    throw err;
  }
}

async function csrfHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  try {
    const resp = await axios.get('/api/auth/csrf-token');
    const token = resp.data?.csrf_token;
    if (token) headers['X-CSRF-Token'] = token;
  } catch {
    // No CSRF token (e.g. bearer auth) — the backend dependency is flexible.
  }
  return headers;
}

/** List the current user's egress connections (tokens are never returned). */
export async function listConnections(): Promise<EgressConnection[]> {
  const resp = await axios.get('/api/egress-auth/connections');
  return resp.data as EgressConnection[];
}

/** List egress-enabled servers the current user can access (for the dropdown). */
export async function listAvailableServers(): Promise<AvailableEgressServer[]> {
  const resp = await axios.get('/api/egress-auth/available-servers');
  return resp.data as AvailableEgressServer[];
}

export interface ConsentInitiation {
  authorizeUrl: string;
  /** True when the provider's AS only accepts a loopback redirect_uri (e.g.
   * Plane): the browser can never reach this gateway automatically, so the
   * caller must offer the manual code-paste completion step instead of
   * polling for the connection to turn active on its own. */
  manualCompletionRequired: boolean;
  accountId: string | null;
  accountLabel: string | null;
}

/** Thrown by initiateConsent when mode 'new' collides with an existing labeled account (HTTP 409). */
export class AccountLabelConflictError extends Error {}

/**
 * Begin consent for a server; returns the provider authorize URL to open.
 *
 * `accountLabel` is only meaningful when connecting a 2nd+ account to a
 * server that already has a connection — omitted, this starts the legacy
 * account-less consent flow (today's exact behavior). `mode: 'new'` (the
 * default) rejects with AccountLabelConflictError if that label is already
 * taken; pass `mode: 'reconnect'` to repair/re-consent an existing labeled
 * account instead of risking an accidental duplicate.
 */
export async function initiateConsent(
  serverPath: string,
  accountLabel?: string,
  mode: 'new' | 'reconnect' = 'new'
): Promise<ConsentInitiation> {
  const headers = await csrfHeaders();
  try {
    const resp = await axios.post(
      '/api/egress-auth/initiate',
      { server_path: serverPath, account_label: accountLabel || null, mode },
      { headers }
    );
    return {
      authorizeUrl: resp.data.authorize_url as string,
      manualCompletionRequired: Boolean(resp.data.manual_completion_required),
      accountId: (resp.data.account_id as string | null) ?? null,
      accountLabel: (resp.data.account_label as string | null) ?? null,
    };
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      throw new AccountLabelConflictError(
        (err.response.data?.detail as string) || 'That account label is already connected.'
      );
    }
    throw err;
  }
}

/**
 * Complete consent for a provider that required manual code-paste (see
 * ConsentInitiation.manualCompletionRequired). Pass either the full address
 * the browser tried and failed to load, or the code/state pair extracted
 * from it directly.
 */
export async function completeManualConsent(
  serverPath: string,
  redirectUrl: string
): Promise<EgressConnection> {
  const headers = await csrfHeaders();
  const resp = await axios.post(
    '/api/egress-auth/complete-manual',
    { server_path: serverPath, redirect_url: redirectUrl },
    { headers }
  );
  return resp.data as EgressConnection;
}

/**
 * Disconnect (revoke + delete the vault entry) for a (provider, server).
 * `accountId` (optional) targets one labeled account; omitted targets the
 * legacy account-less entry and never touches any labeled sibling.
 */
export async function disconnect(
  provider: string,
  serverPath: string,
  accountId?: string | null
): Promise<void> {
  const headers = await csrfHeaders();
  const path = serverPath.replace(/^\//, '');
  const params = accountId ? { account_id: accountId } : undefined;
  await axios.delete(`/api/egress-auth/connections/${provider}/${path}`, { headers, params });
}

/**
 * Mark one account as the default an agent's tool call auto-uses for
 * (provider, server_path). Explicit, human-driven action only — never a side
 * effect of connecting/reconnecting. `accountId: null` selects the legacy
 * account-less entry as default.
 */
export async function setDefaultAccount(
  provider: string,
  serverPath: string,
  accountId: string | null
): Promise<void> {
  const headers = await csrfHeaders();
  const path = serverPath.replace(/^\//, '');
  await axios.post(
    `/api/egress-auth/connections/${provider}/${path}/default`,
    { account_id: accountId },
    { headers }
  );
}

/**
 * Opt one connection in/out of the background tool-discovery job for its
 * (provider, server_path). Explicit, human-driven action only — never a side
 * effect of connecting/reconnecting. `accountId: null` selects the legacy
 * account-less entry.
 */
export async function setToolDiscoveryConsent(
  provider: string,
  serverPath: string,
  accountId: string | null,
  consent: boolean
): Promise<void> {
  const headers = await csrfHeaders();
  const path = serverPath.replace(/^\//, '');
  await axios.post(
    `/api/egress-auth/connections/${provider}/${path}/tool-discovery-consent`,
    { account_id: accountId, consent },
    { headers }
  );
}

/** Whether the egress-auth feature is enabled (drives nav/page visibility). */
export async function isEgressAuthEnabled(): Promise<boolean> {
  try {
    // The connections endpoint 404s when the feature is disabled.
    await axios.get('/api/egress-auth/connections');
    return true;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return false;
    // Any other error (401/500): assume enabled so the page can surface it.
    return true;
  }
}
